import { useEffect } from 'react'
import useBodyScrollLock from '../../../../../hooks/useBodyScrollLock'
import AppActionMenu from '../../../../../components/AppActionMenu'
import {
  printStudentsReport,
  printIdCards,
  printStudentsWithoutPhoto,
  printBatchStrength,
  printEnvelope,
  printStudentLabels,
  printParentLabels,
} from '../../utils/exportUtils'
import { appToast } from '../../../../../components/AppToast'

const REPORTS = [
  {
    key: 'student-list',
    title: 'List of Students',
    description: 'List of Students Branchwise, Coursewise and Batchwise',
    handler: printStudentsReport,
    label: 'Student List',
  },
  {
    key: 'id-card',
    title: 'ID Card',
    description: 'Prints the ID Card in Vertical Format without Subjects',
    handler: printIdCards,
    label: 'ID Card',
  },
  {
    key: 'no-photo',
    title: 'List of Students Without Photograph',
    description: 'List of the Students who has not given their Photographs',
    handler: printStudentsWithoutPhoto,
    label: 'Students Without Photo',
  },
  {
    key: 'batch-strength',
    title: 'Batch Wise Strength',
    description: 'Shows the number of Students Batch Wise',
    handler: printBatchStrength,
    label: 'Batch Strength',
  },
  {
    key: 'envelope',
    title: 'Envelope',
    description: 'Shows the Name and Address of the Students',
    handler: printEnvelope,
    label: 'Envelope',
  },
  {
    key: 'label-students',
    title: 'Label-Students',
    description: 'Address labels with Students Name',
    handler: printStudentLabels,
    label: 'Student Labels',
  },
  {
    key: 'label-parents',
    title: 'Label - Parents',
    description: 'Address labels with Parents Name',
    handler: printParentLabels,
    label: 'Parent Labels',
  },
]

export default function PrintReportModal({ isOpen, onClose, students = [] }) {
  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const handlePrint = (report) => {
    if (!students || students.length === 0) {
      appToast.warning('No student records to print.')
      return
    }
    if (report.key === 'no-photo') {
      const noPhoto = students.filter((s) => !s.photoUrl)
      if (noPhoto.length === 0) {
        appToast.warning('All students have photographs on file.')
        return
      }
      report.handler(noPhoto, report.label)
    } else {
      report.handler(students, report.label)
    }
    onClose?.()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/45 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <div
        className="flex h-[85vh] max-h-[740px] w-full max-w-[860px] flex-col overflow-hidden rounded-2xl bg-surface-container-lowest shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Blue Banner Header ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between bg-primary px-8 py-5">
          <div>
            <h2 className="text-xl font-extrabold text-on-primary tracking-tight">Reports</h2>
            <p className="mt-0.5 text-sm text-on-primary/80">
              Select a report type to generate a print preview
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-on-primary/15 px-3 py-1 text-xs font-bold text-on-primary">
              {students.length} record{students.length !== 1 ? 's' : ''}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-on-primary/70 transition-colors hover:bg-on-primary/10 hover:text-on-primary"
            >
              <span className="material-symbols-outlined text-xl">close</span>
            </button>
          </div>
        </div>

        {/* ── Report List ────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {REPORTS.map((report, index) => {
            const isEven = index % 2 === 0
            return (
              <div
                key={report.key}
                className={`flex items-center justify-between gap-6 px-8 py-5 transition-colors hover:bg-primary/5 ${
                  isEven ? 'bg-surface-container-lowest' : 'bg-surface-container-low/50'
                } ${index < REPORTS.length - 1 ? 'border-b border-outline-variant/15' : ''}`}
              >
                {/* Left: Icon + Title + Description */}
                <div className="flex items-start gap-4 min-w-0">
                  <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <span className="material-symbols-outlined text-xl">description</span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-bold text-on-surface leading-tight">
                      {report.title}
                    </h3>
                    <p className="mt-1 text-sm text-on-surface-variant leading-relaxed">
                      {report.description}
                    </p>
                  </div>
                </div>

                {/* Right: Print Report button */}
                <div className="shrink-0">
                  <AppActionMenu
                    triggerLabel="Print Report"
                    triggerIcon="print"
                    showChevron={true}
                    triggerClassName="bg-primary text-on-primary hover:shadow-md"
                    menuClassName="min-w-[160px]"
                    items={[
                      { key: 'print', label: 'Print', icon: 'print' },
                    ]}
                    onSelect={() => handlePrint(report)}
                  />
                </div>
              </div>
            )
          })}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-end border-t border-outline-variant/20 px-8 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-outline-variant/40 bg-surface-container-low px-6 py-2.5 text-sm font-bold text-on-surface-variant transition-colors hover:bg-surface-container-high"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
